import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';

import './Toast.scss';

import { Toast as PrimeReactToast } from 'primereact/toast';
import type { ToastMessage } from 'primereact/toast';

import { ReactComponent as SuccessIcon } from '../../assets/images/success-icon.svg';
import { ReactComponent as ErrorIcon } from '../../assets/images/error-icon.svg';
import { ReactComponent as WarningIcon } from '../../assets/images/warning-icon.svg';

import moment from 'moment/moment';


const TOAST_DURATION_SECONDS = 5;


type ToastVariant = 'success' | 'error' | 'warning';


type ToastPosition =
    | 'top-left'
    | 'top-center'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-center'
    | 'bottom-right'
    | 'center';


type ToastPayload = {
    title?: string;
    detail?: string;
    message?: string;
    error?: string;
    code?: string;

    trace_id?: string;
    traceId?: string;

    err?: {
        trace_id?: string;
        detail?: string;
        title?: string;
    };

    response?: {
        data?: {
            trace_id?: string;
            detail?: string;
            title?: string;
        };
    };

    data?: {
        trace_id?: string;
        detail?: string;
        title?: string;
    };
};


type DisplayMessage =
    | string
    | ToastPayload
    | number
    | boolean
    | null
    | undefined;


type CustomToastMessage = ToastMessage & {
    id: string;
    summary: string;
    time: string;
    variant: ToastVariant;
};


export type ToastHandle = {
    success: (
        message: DisplayMessage,
        position?: ToastPosition,
    ) => void;

    error: (
        message: DisplayMessage,
        position?: ToastPosition,
    ) => void;

    warning: (
        message: DisplayMessage,
        position?: ToastPosition,
    ) => void;
};


type ToastContentWithTimerProps = {
    message: CustomToastMessage;
    messageRef: React.RefObject<PrimeReactToast | null>;
};


const cleanTraceId = (
    value: unknown,
): string => {
    if (!value) {
        return '';
    }

    return String(value)
        .replace(
            /traceId\s*[:=]\s*[a-zA-Z0-9-]+/g,
            '',
        )
        .replace(/\s{2,}/g, ' ')
        .trim();
};


const getDisplayMessage = (
    value: DisplayMessage,
): string => {
    if (!value) {
        return '';
    }

    if (typeof value === 'string') {
        return value;
    }

    if (typeof value === 'object') {
        const payload = value as ToastPayload;

        const traceId =
            payload.trace_id ??
            payload.traceId ??
            payload.err?.trace_id ??
            payload.response?.data?.trace_id ??
            payload.data?.trace_id;

        const message =
            payload.detail ??
            payload.title ??
            payload.message ??
            payload.err?.detail ??
            payload.err?.title ??
            payload.response?.data?.detail ??
            payload.response?.data?.title ??
            payload.data?.detail ??
            payload.data?.title ??
            payload.error ??
            payload.code ??
            'Server error';

        const cleanedMessage =
            cleanTraceId(message);

        if (traceId) {
            return `${cleanedMessage} | Trace ID: ${traceId}`;
        }

        return cleanedMessage;
    }

    return String(value);
};


const ToastContentWithTimer = ({
    message,
    messageRef,
}: ToastContentWithTimerProps): React.ReactElement => {
    const [seconds, setSeconds] =
        useState<number>(TOAST_DURATION_SECONDS);

    useEffect(() => {
        setSeconds(TOAST_DURATION_SECONDS);

        const interval = window.setInterval(() => {
            setSeconds(previous => previous - 1);
        }, 1000);

        return () => {
            window.clearInterval(interval);
        };
    }, [message.id]);

    useEffect(() => {
        if (seconds <= 0) {
            messageRef.current?.remove(message);
        }
    }, [
        seconds,
        message,
        messageRef,
    ]);

    return (
        <div className="toast-content-wrapper">
            <div className="toast-content">
                {message.variant === 'success' && (
                    <SuccessIcon
                        className="status-icon"
                    />
                )}

                {message.variant === 'error' && (
                    <ErrorIcon
                        className="status-icon"
                    />
                )}

                {message.variant === 'warning' && (
                    <WarningIcon
                        className="status-icon"
                    />
                )}

                <div className="toast-subcontent">
                    <div className="text">
                        {getDisplayMessage(
                            message.summary,
                        )}
                    </div>

                    <div className="time-wrapper">
                        <div className="time">
                            {message.time}
                        </div>
                    </div>
                </div>
            </div>

            <div
                className={[
                    'left-border',
                    `variant-${message.variant}`,
                ].join(' ')}
            />

            <div className="timer-wrapper">
                <div className="timer">
                    {seconds}s
                </div>
            </div>
        </div>
    );
};


const Toast = forwardRef<ToastHandle>(
    (
        _props,
        ref,
    ): React.ReactElement => {
        const messageRef =
            useRef<PrimeReactToast | null>(null);

        const [position, setPosition] =
            useState<ToastPosition>('bottom-right');

        const showMessage = (
            message: DisplayMessage,
            variant: ToastVariant,
            toastPosition: ToastPosition = 'bottom-right',
        ): void => {
            setPosition(toastPosition);

            const toastMessage: CustomToastMessage = {
                id: crypto.randomUUID(),
                summary: getDisplayMessage(message),
                time: moment().format('HH:mm:ss'),
                variant,
            };

            messageRef.current?.show(
                toastMessage,
            );
        };


        const success = (
            message: DisplayMessage,
            toastPosition: ToastPosition = 'bottom-right',
        ): void => {
            showMessage(
                message,
                'success',
                toastPosition,
            );
        };


        const error = (
            message: DisplayMessage,
            toastPosition: ToastPosition = 'bottom-right',
        ): void => {
            showMessage(
                message,
                'error',
                toastPosition,
            );
        };


        const warning = (
            message: DisplayMessage,
            toastPosition: ToastPosition = 'bottom-right',
        ): void => {
            showMessage(
                message,
                'warning',
                toastPosition,
            );
        };


        useImperativeHandle(
            ref,
            () => ({
                success,
                error,
                warning,
            }),
        );


        return (
            <PrimeReactToast
                ref={messageRef}
                position={position}
                content={({ message }) => (
                    <ToastContentWithTimer
                        message={
                            message as CustomToastMessage
                        }
                        messageRef={messageRef}
                    />
                )}
            />
        );
    },
);


Toast.displayName = 'Toast';


export default Toast;